import { computed, ref, watch, onMounted, onBeforeUnmount, useHost, type Ref, type ComputedRef } from "vue";
import type { ToastProps, ToastColor, ToastVariant } from "./toast.types";
import { createEventDispatcher } from "@/runtime/shared/events";
import { parseBoolAttr } from "@/runtime/shared/attrs";
import { TOAST_PAUSE_EVENT, TOAST_RESUME_EVENT } from "./toast.api";

export interface UseToastOptions {
  props: Readonly<ToastProps>;
}

const SWIPE_THRESHOLD = 100;

const TYPE_ICONS: Record<string, string> = {
  success: "circle-check",
  error: "circle-close",
  warning: "error-circle",
  info: "info-circle",
  loading: "refresh",
};

const TYPE_TO_COLOR: Record<string, ToastColor> = {
  success: "success",
  error: "error",
  warning: "warning",
  info: "info",
};

const useToastUiState = (props: Readonly<ToastProps>) => {
  const duration = computed(() => {
    if (props.type === "error") return 0;
    const d = Number(props.duration);
    return Number.isFinite(d) ? d : 4000;
  });

  const isDismissible = computed(() => props.dismissible !== false && props.dismissible !== "false");

  const showCloseButton = computed(() => {
    const v = props.closeButton;
    if (v === false || v === "false") return false;
    if (v === "" || v === true || v === "true") return true;
    return isDismissible.value;
  });

  const cardColor = computed<ToastColor>(() => props.color || TYPE_TO_COLOR[props.type ?? ""] || "neutral");
  const cardVariant = computed<ToastVariant>(
    () => props.variant || (parseBoolAttr(props.richColors, false) ? "soft" : "subtle"),
  );
  const iconName = computed(() => TYPE_ICONS[props.type ?? ""] ?? undefined);

  const descriptionClamp = computed(() => {
    if (parseBoolAttr(props.expanded, false)) return;
    return parseBoolAttr(props.stacked, false) ? "line-clamp-1" : "line-clamp-2";
  });

  const toastCount = computed(() => {
    const v = Number(props.count);
    return Number.isFinite(v) && v > 1 ? v : 0;
  });

  const ariaRole = computed(() => (props.type === "error" ? "alert" : "status"));
  const ariaLive = computed(() => (props.type === "error" ? "assertive" : "polite"));

  return {
    duration,
    isDismissible,
    showCloseButton,
    cardColor,
    cardVariant,
    iconName,
    descriptionClamp,
    toastCount,
    ariaRole,
    ariaLive,
  };
};

interface ToastTimerOptions {
  duration: () => number;
  isRemoving: Ref<boolean>;
  globalPaused: Ref<boolean>;
  onTimeout: () => void;
}

const useToastTimer = (opts: ToastTimerOptions) => {
  let timerId: ReturnType<typeof setTimeout> | null = null;
  let startTime = 0;
  let remaining = 0;

  const start = (): void => {
    if (opts.isRemoving.value) return;
    const d = opts.duration();
    if (d <= 0) return;
    remaining = remaining > 0 ? remaining : d;
    startTime = Date.now();
    timerId = setTimeout(opts.onTimeout, remaining);
  };

  const pause = (): void => {
    if (timerId !== null) {
      clearTimeout(timerId);
      timerId = null;
      remaining = Math.max(0, remaining - (Date.now() - startTime));
    }
  };

  const reset = (): void => {
    pause();
    remaining = 0;
    if (!opts.globalPaused.value) start();
  };

  const cleanup = (): void => {
    if (timerId !== null) clearTimeout(timerId);
  };

  return { start, pause, reset, cleanup };
};

const useSwipeDismiss = (isDismissible: ComputedRef<boolean>, dismiss: () => void) => {
  const isSwiping = ref(false);
  const swipeOffset = ref(0);
  let startX = 0;

  const handlePointerDown = (e: PointerEvent): void => {
    if (!isDismissible.value) return;
    startX = e.clientX;
    isSwiping.value = true;
    (e.currentTarget as HTMLElement)?.setPointerCapture(e.pointerId);
  };

  const handlePointerMove = (e: PointerEvent): void => {
    if (!isSwiping.value) return;
    swipeOffset.value = e.clientX - startX;
  };

  const handlePointerUp = (): void => {
    if (!isSwiping.value) return;
    isSwiping.value = false;
    if (Math.abs(swipeOffset.value) >= SWIPE_THRESHOLD) {
      swipeOffset.value = 0;
      dismiss();
    } else {
      swipeOffset.value = 0;
    }
  };

  const swipeStyle = computed(() => {
    const base: Record<string, string | undefined> = { touchAction: "pan-y" };
    if (swipeOffset.value === 0) return base;
    return {
      ...base,
      transform: `translateX(${swipeOffset.value}px)`,
      opacity: `${1 - Math.abs(swipeOffset.value) / (SWIPE_THRESHOLD * 2)}`,
      transition: isSwiping.value ? "none" : undefined,
    };
  });

  return { swipeStyle, handlePointerDown, handlePointerMove, handlePointerUp };
};

const useGlobalPauseResume = (
  timer: { start: () => void; pause: () => void },
  isRemoving: Ref<boolean>,
  globalPaused: Ref<boolean>,
): void => {
  const onPause = (): void => {
    globalPaused.value = true;
    timer.pause();
  };
  const onResume = (): void => {
    globalPaused.value = false;
    if (!isRemoving.value) timer.start();
  };

  onMounted(() => {
    document.addEventListener(TOAST_PAUSE_EVENT, onPause);
    document.addEventListener(TOAST_RESUME_EVENT, onResume);
  });

  onBeforeUnmount(() => {
    document.removeEventListener(TOAST_PAUSE_EVENT, onPause);
    document.removeEventListener(TOAST_RESUME_EVENT, onResume);
  });
};

export const useToast = (options: UseToastOptions) => {
  const { props } = options;
  const host = useHost();
  const isRemoving = ref(false);
  const globalPaused = ref(false);
  const ui = useToastUiState(props);
  const dispatchEvent = createEventDispatcher(host);

  const timer = useToastTimer({
    duration: () => ui.duration.value,
    isRemoving,
    globalPaused,
    onTimeout: () => dismiss("timeout"),
  });

  const dismiss = (reason: "user" | "timeout" = "user"): void => {
    if (isRemoving.value) return;
    isRemoving.value = true;
    timer.pause();
    dispatchEvent("dismiss", { id: props.toastId, reason });
  };

  const swipe = useSwipeDismiss(ui.isDismissible, () => dismiss("user"));
  useGlobalPauseResume(timer, isRemoving, globalPaused);

  onMounted(() => timer.start());

  watch(
    () => Number(props.count) || 0,
    (next, prev) => {
      if (next > prev) timer.reset();
    },
  );

  onBeforeUnmount(() => timer.cleanup());

  const baseClasses =
    "e-toast pointer-events-auto relative w-[356px] rounded-lg shadow-lg transition-[transform,opacity] duration-300 ease-out";

  return {
    ...ui,
    isRemoving,
    baseClasses,
    ...swipe,
    handleMouseEnter: timer.pause,
    handleMouseLeave: (): void => {
      if (!isRemoving.value && !globalPaused.value) timer.start();
    },
    handleClose: (): void => {
      if (ui.isDismissible.value) dismiss("user");
    },
  };
};
